import { jsx } from "react/jsx-runtime";
import { createContext, useMemo, useContext } from "react";
import { useForm } from "./useForm.js";
function createFormHookContexts() {
  const fieldContext = createContext(null);
  function useFieldContext() {
    const field = useContext(fieldContext);
    if (!field) {
      throw new Error(
        "`fieldContext` only works when within a `fieldComponent` passed to `createFormHook`"
      );
    }
    return field;
  }
  const formContext = createContext(null);
  function useFormContext() {
    const form = useContext(formContext);
    if (!form) {
      throw new Error(
        "`formContext` only works when within a `formComponent` passed to `createFormHook`"
      );
    }
    return form;
  }
  return { fieldContext, useFieldContext, useFormContext, formContext };
}
function createFormHook({
  fieldComponents,
  fieldContext,
  formContext,
  formComponents
}) {
  function useAppForm(props) {
    const form = useForm(props);
    const AppForm = useMemo(() => {
      return ({ children }) => {
        return /* @__PURE__ */ jsx(formContext.Provider, { value: form, children });
      };
    }, [form]);
    const AppField = useMemo(() => {
      return ({ children, ...props2 }) => {
        return /* @__PURE__ */ jsx(form.Field, { ...props2, children: (field) => (
          // eslint-disable-next-line @eslint-react/no-context-provider
          /* @__PURE__ */ jsx(fieldContext.Provider, { value: field, children: children(Object.assign(field, fieldComponents)) })
        ) });
      };
    }, [form]);
    const extendedForm = useMemo(() => {
      return Object.assign(form, {
        AppField,
        AppForm,
        ...formComponents
      });
    }, [form, AppField, AppForm]);
    return extendedForm;
  }
  function withForm({
    render,
    props
  }) {
    return (innerProps) => render({ ...props, ...innerProps });
  }
  return {
    useAppForm,
    withForm
  };
}
export {
  createFormHook,
  createFormHookContexts
};
//# sourceMappingURL=createFormHook.js.map
